// Enhanced DataTableSettings.jsx with server-side pagination support
import React, { useState, useEffect } from 'react';
import { Pencil, Trash2, Search, Plus, ChevronLeft, ChevronRight, Filter, ArrowUp, ArrowDown } from 'lucide-react';

const DataTableSettings = ({
  title,
  subtitle,
  data,
  columns,
  onEdit,
  onDelete,
  onCreate,
  onRowClick,
  rowClickEnabled = false,
  loading = false,
  emptyMessage = "Aucune donnée disponible",
  // ✅ NEW: Server-side pagination props
  pagination = null,
  // ✅ NEW: Server-side search props
  search = null,
  // ✅ NEW: Server-side sort props
  sort = null
}) => {
  // ✅ LOCAL STATE: Only for client-side mode (when server-side props not provided)
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [localCurrentPage, setLocalCurrentPage] = useState(1);
  const [localItemsPerPage, setLocalItemsPerPage] = useState(10);
  const [localSortColumn, setLocalSortColumn] = useState(null);
  const [localSortDirection, setLocalSortDirection] = useState('asc');

  // ✅ DETERMINE MODE: Server-side vs client-side
  const isServerSide = pagination !== null;
  
  // ✅ USE SERVER-SIDE OR LOCAL STATE
  const searchTerm = search?.value ?? localSearchTerm;
  const currentPage = pagination?.currentPage ?? localCurrentPage;
  const itemsPerPage = pagination?.itemsPerPage ?? localItemsPerPage;
  const totalPages = pagination?.totalPages ?? Math.ceil((data?.length || 0) / localItemsPerPage);
  const totalItems = pagination?.totalItems ?? (data?.length || 0);
  const sortColumn = sort?.column ?? localSortColumn;
  const sortDirection = sort?.direction ?? localSortDirection;

  // ✅ CLIENT-SIDE FILTERING & SORTING (only when not server-side)
  const processedData = React.useMemo(() => {
    if (isServerSide) {
      return data || []; // Server handles filtering/sorting
    }

    let filtered = data || [];

    // Client-side search
    if (searchTerm && searchTerm.length > 0) {
      filtered = filtered.filter(item => {
        return columns.some(column => {
          if (!column.searchable) return false;
          const value = item[column.accessor];
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(searchTerm.toLowerCase());
        });
      });
    }

    // Client-side sorting
    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        const valueA = a[sortColumn];
        const valueB = b[sortColumn];
        
        if (valueA === valueB) return 0;
        if (valueA === null || valueA === undefined) return 1;
        if (valueB === null || valueB === undefined) return -1;
        
        const result = typeof valueA === 'string' 
          ? valueA.localeCompare(valueB) 
          : valueA - valueB;
          
        return sortDirection === 'asc' ? result : -result;
      });
    }

    return filtered;
  }, [data, searchTerm, sortColumn, sortDirection, isServerSide, columns]);

  // ✅ CLIENT-SIDE PAGINATION (only when not server-side)
  const paginatedData = React.useMemo(() => {
    if (isServerSide) {
      return processedData; // Server handles pagination
    }

    const startIndex = (currentPage - 1) * itemsPerPage;
    return processedData.slice(startIndex, startIndex + itemsPerPage);
  }, [processedData, currentPage, itemsPerPage, isServerSide]);

  // ✅ HANDLERS
  const handleSearch = (newSearchTerm) => {
    if (isServerSide && search?.onChange) {
      search.onChange(newSearchTerm);
    } else {
      setLocalSearchTerm(newSearchTerm);
      setLocalCurrentPage(1); // Reset to first page
    }
  };

  const handleSort = (accessor) => {
    if (isServerSide && sort?.onSort) {
      sort.onSort(accessor);
    } else {
      if (localSortColumn === accessor) {
        setLocalSortDirection(localSortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setLocalSortColumn(accessor);
        setLocalSortDirection('asc');
      }
      setLocalCurrentPage(1); // Reset to first page
    }
  };

  const handlePageChange = (page) => {
    if (isServerSide && pagination?.onPageChange) {
      pagination.onPageChange(page);
    } else {
      setLocalCurrentPage(page);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    if (isServerSide && pagination?.onItemsPerPageChange) {
      pagination.onItemsPerPageChange(newItemsPerPage);
    } else {
      setLocalItemsPerPage(newItemsPerPage);
      setLocalCurrentPage(1);
    }
  };

  const handleRowClick = (row) => {
    if (rowClickEnabled && onRowClick) {
      onRowClick(row);
    }
  };

  // ✅ CALCULATE DISPLAY VALUES
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalItems);
  const displayData = paginatedData;

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Header */}
      <div className="p-5 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
            {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
          </div>
          {onCreate && (
            <button
              onClick={onCreate}
              className="flex items-center px-4 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition"
            >
              <Plus size={16} className="mr-2" />
              Ajouter
            </button>
          )}
        </div>
      </div>

      {/* Search and Controls */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center flex-1">
            <div className="relative flex-grow max-w-md">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={search?.placeholder || "Rechercher..."}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-600"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
            {loading && (
              <div className="ml-3 flex items-center text-sm text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500 mr-2"></div>
                Chargement...
              </div>
            )}
          </div>
          
          {/* Items per page selector */}
          <div className="ml-4 flex items-center space-x-2">
            <span className="text-sm text-gray-700">Afficher:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-green-600"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-gray-700">par page</span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.accessor}
                  onClick={() => column.sortable && handleSort(column.accessor)}
                  className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    column.sortable 
                      ? 'cursor-pointer hover:bg-gray-100 select-none' 
                      : ''
                  }`}
                >
                  <div className="flex items-center">
                    {column.Header}
                    {column.sortable && (
                      <div className="ml-1 flex flex-col">
                        {sortColumn === column.accessor ? (
                          sortDirection === 'asc' ? (
                            <ArrowUp size={14} className="text-gray-600" />
                          ) : (
                            <ArrowDown size={14} className="text-gray-600" />
                          )
                        ) : (
                          <div className="opacity-40">
                            <ArrowUp size={12} className="text-gray-400" />
                            <ArrowDown size={12} className="text-gray-400 -mt-1" />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </th>
              ))}
              {(onEdit || onDelete) && (
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {!loading && displayData.length > 0 ? (
              displayData.map((row, index) => (
                <tr
                  key={row.id || index}
                  onClick={() => handleRowClick(row)}
                  className={`${
                    rowClickEnabled
                      ? 'cursor-pointer hover:bg-gray-50'
                      : 'hover:bg-gray-50'
                  } transition-colors`}
                >
                  {columns.map((column) => (
                    <td key={column.accessor} className="px-6 py-4 whitespace-nowrap">
                      {column.Cell
                        ? column.Cell({ value: row[column.accessor], row })
                        : row[column.accessor] || '-'}
                    </td>
                  ))}
                  {(onEdit || onDelete) && (
                    <td
                      className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {onEdit && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit(row);
                          }}
                          className="text-indigo-600 hover:text-indigo-900 mr-3 p-1 rounded hover:bg-indigo-100 transition-colors"
                          title="Modifier"
                        >
                          <Pencil size={16} />
                        </button>
                      )}
                      {onDelete && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(row);
                          }}
                          className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-100 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))
            ) : loading ? (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  className="px-6 py-8 text-center"
                >
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mr-3"></div>
                    <span className="text-gray-500">Chargement des données...</span>
                  </div>
                </td>
              </tr>
            ) : (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  className="px-6 py-8 text-center text-gray-500"
                >
                  <div className="flex flex-col items-center">
                    <svg className="w-12 h-12 text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-4.5a2 2 0 00-2 2.5v.5a2 2 0 01-2 2H8a2 2 0 01-2-2v-.5a2 2 0 00-2-2.5H4m16 0v1a2 2 0 01-2 2h-4a2 2 0 01-2-2v-1" />
                    </svg>
                    {emptyMessage}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200 bg-gray-50">
          <div className="flex-1 flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-700">
                Affichage de <span className="font-medium">{startIndex}</span> à{' '}
                <span className="font-medium">{endIndex}</span>{' '}
                sur <span className="font-medium">{totalItems.toLocaleString()}</span> résultats
              </p>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* First page */}
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className={`px-3 py-1 text-sm border rounded ${
                  currentPage === 1
                    ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-400'
                    : 'hover:bg-gray-100 bg-white text-gray-700'
                }`}
              >
                Premier
              </button>

              {/* Previous page */}
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-3 py-2 border rounded-md ${
                  currentPage === 1
                    ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-400'
                    : 'hover:bg-gray-100 bg-white text-gray-700'
                }`}
              >
                <ChevronLeft size={16} />
              </button>

              {/* Page numbers */}
              <div className="flex space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-1 text-sm border rounded ${
                        pageNum === currentPage
                          ? 'bg-green-600 text-white border-green-600'
                          : 'hover:bg-gray-100 bg-white text-gray-700'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              {/* Next page */}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-3 py-2 border rounded-md ${
                  currentPage === totalPages
                    ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-400'
                    : 'hover:bg-gray-100 bg-white text-gray-700'
                }`}
              >
                <ChevronRight size={16} />
              </button>

              {/* Last page */}
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 text-sm border rounded ${
                  currentPage === totalPages
                    ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-400'
                    : 'hover:bg-gray-100 bg-white text-gray-700'
                }`}
              >
                Dernier
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTableSettings;